'use client';

import { Handshake, ThumbsDown, ThumbsUp } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useEffect, useState, useTransition } from 'react';

import { Controller, useForm, useWatch, zodResolver } from '@/components/rhf';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { Field, FieldError, FieldGroup, FieldSet } from '@/components/ui/field';
import { Item, ItemActions, ItemContent, ItemMedia, ItemTitle } from '@/components/ui/item';
import { Textarea } from '@/components/ui/textarea';
import {
  type SubmitFeedback,
  type SubmitFeedbackResponse,
  SubmitFeedbackResponseSchema,
  SubmitFeedbackSchema,
} from '@/lib/feedback';

import { Spinner } from '../ui/spinner';

const feedbackResult = SubmitFeedbackSchema.extend({
  response: SubmitFeedbackResponseSchema,
});

function useSubmissionStorage<Result>(id: string, validate: (v: unknown) => Result | null) {
  const storageKey = `docs-feedback-${id}`;
  const [value, setValue] = useState<Result | null>(null);

  useEffect(() => {
    const item = localStorage.getItem(storageKey);
    if (item === null) return;
    const validated = validate(JSON.parse(item));

    // reading localStorage must happen after mount to avoid hydration mismatches
    // oxlint-disable-next-line react/set-state-in-effect
    if (validated !== null) setValue(validated);
  }, [storageKey, validate]);

  return {
    previous: value,
    setPrevious(result: Result | null) {
      if (result) localStorage.setItem(storageKey, JSON.stringify(result));
      else localStorage.removeItem(storageKey);

      setValue(result);
    },
  };
}

/**
 * A feedback component to be attached at the end of page
 */
export function Feedback({
  onSendAction,
}: {
  onSendAction: (feedback: SubmitFeedback) => Promise<SubmitFeedbackResponse>;
}) {
  const url = usePathname();
  const { previous, setPrevious } = useSubmissionStorage(url, (v) => {
    const result = feedbackResult.safeParse(v);
    return result.success ? result.data : null;
  });
  const [isPending, startTransition] = useTransition();
  const form = useForm({ resolver: zodResolver(SubmitFeedbackSchema), defaultValues: { url, message: '' } });
  const opinion = useWatch({ control: form.control, name: 'opinion' });

  function submit(data: SubmitFeedback) {
    startTransition(async () => {
      const response = await onSendAction(data);
      setPrevious({
        response,
        ...data,
      });
      form.reset({ url, message: '' });
    });
  }

  const activeOpinion = previous?.opinion ?? opinion;

  return (
    <Collapsible
      open={Boolean(opinion) || previous !== null}
      onOpenChange={(v) => {
        if (!v) form.resetField('opinion');
      }}
    >
      <div className='flex flex-row items-center gap-2'>
        <p className='pe-2 text-sm font-medium'>How is this guide?</p>
        <Button
          disabled={previous !== null}
          variant={activeOpinion === 'good' ? 'default' : 'outline'}
          className='rounded-full'
          onClick={() => form.setValue('opinion', 'good')}
        >
          <ThumbsUp />
          Good
        </Button>
        <Button
          disabled={previous !== null}
          variant={activeOpinion === 'bad' ? 'default' : 'outline'}
          className='rounded-full'
          onClick={() => form.setValue('opinion', 'bad')}
        >
          <ThumbsDown />
          Bad
        </Button>
      </div>
      <CollapsibleContent className='mt-3'>
        {previous ? (
          <Item variant='muted'>
            <ItemMedia variant='image'>
              <Handshake />
            </ItemMedia>
            <ItemContent>
              <ItemTitle className='text-muted-foreground'>Thank you for your feedback!</ItemTitle>
            </ItemContent>
            <ItemActions>
              {previous.response?.url && (
                <a href={previous.response.url} rel='noreferrer noopener' target='_blank'>
                  <Button size='sm' className='text-xs'>
                    View Submission
                  </Button>
                </a>
              )}

              <Button
                variant='secondary'
                size='sm'
                className='text-xs'
                onClick={() => {
                  form.setValue('opinion', previous.opinion);
                  setPrevious(null);
                }}
              >
                Submit Again
              </Button>
            </ItemActions>
          </Item>
        ) : (
          <form className='flex flex-col gap-3' onSubmit={form.handleSubmit(submit)}>
            <FieldSet>
              <FieldGroup>
                <Controller
                  name='message'
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field>
                      <Textarea
                        {...field}
                        autoFocus
                        required
                        className='resize-none'
                        placeholder='Leave your feedback...'
                        onKeyDown={(e) => {
                          if (!e.shiftKey && e.key === 'Enter') {
                            form.handleSubmit(submit)();
                          }
                        }}
                      />
                      {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                    </Field>
                  )}
                />
                <Field orientation='horizontal'>
                  <Button
                    type='submit'
                    variant='outline'
                    disabled={isPending || form.formState.isSubmitting || !form.formState.isDirty}
                  >
                    {isPending || form.formState.isSubmitting ? (
                      <>
                        <Spinner />
                        Submitting...
                      </>
                    ) : (
                      'Submit'
                    )}
                  </Button>
                </Field>
              </FieldGroup>
            </FieldSet>
          </form>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
